import * as echarts from 'echarts'

export function needsDateLabel(date, index) {
  return date.getHours() === 0 || index === 1
}

export function determineAmPm(date) {
  const hours = date.getHours()
  const hour12 = hours % 12 || 12
  const ampm = hours >= 12 ? 'PM' : 'AM'

  const minutes = date.getMinutes()
  return minutes ? `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}` : `${hour12}${ampm}`
}

// Get custom CSS properties
const lineColor = getComputedStyle(document.documentElement)
  .getPropertyValue('--time-series-chart-red-color').trim() || '#911315'
export const SPLIT_LINE_COLOR = getComputedStyle(document.documentElement)
  .getPropertyValue('--time-series-chart-split-line-color').trim() || '#CBCCD4'
export const NORMAL_LABEL_COLOR = getComputedStyle(document.documentElement)
  .getPropertyValue('--time-series-chart-normal-label-color').trim() || '#9E9E9E'
export const DARK_LABEL_COLOR = getComputedStyle(document.documentElement)
  .getPropertyValue('--time-series-chart-dark-label-color').trim() || '#646669'
export const LABEL_FONT_SIZE = getComputedStyle(document.documentElement)
  .getPropertyValue('--time-series-chart-label-font-size').trim() || '14px'

export const ECHART_OPTION_GRID_DEFAULTS = {
  left: '16px',
  right: '20px',
  top: '16px',
  bottom: '16px',
  containLabel: true // This keeps labels but minimizes space
}

export const ECHART_OPTION_X_AXIS_DEFAULTS = {
  type: 'time',
  boundaryGap: false,
  axisLine: {
    show: false
  },
  axisTick: {
    show: false
  },
  axisLabel: {
    hideOverlap: true,
    fontSize: LABEL_FONT_SIZE,
    formatter: function (value, index) {
      const date = new Date(value)

      // Show the full date for the left-most label and at midnight
      if (needsDateLabel(date, index)) {
        return echarts.format.formatTime('MM/dd/yy', date)
      }

      return `${determineAmPm(date)}`
    },
    color: function (value, index) {
      const date = new Date(value)

      // Darker color for date labels vs. time labels to make it easier to read
      return needsDateLabel(date, index) ? DARK_LABEL_COLOR : NORMAL_LABEL_COLOR
    }
  }
}

export const ECHART_OPTION_Y_AXIS_DEFAULTS = {
  type: 'value',
  axisLabel: {
    color: NORMAL_LABEL_COLOR,
    fontSize: LABEL_FONT_SIZE
  },
  axisLine: {
    show: false
  },
  splitNumber: 2,
  splitLine: {
    lineStyle: {
      color: SPLIT_LINE_COLOR,
      width: 2
    }
  }
}

// Shared by station-chart-data.js (single-station page) and
// station-group-chart-live.js (group page) so both render charts identically
// without duplicating this logic.

export function coerceOnOffValue (v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0') return false
  }
  return Boolean(v)
}

export function isNumericValue (v) {
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string') {
    const s = v.trim()
    return s !== '' && !Number.isNaN(parseFloat(s))
  }
  return false
}

export function parseAllowedTypes (wrapper) {
  const raw = wrapper.getAttribute('data-allowed-chart-types') || ''
  return raw.split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean)
}

export function detectBestChartType (points) {
  if (!points || points.length === 0) return 'line'
  const values = points.map(p => p.value)
  const allBooleanLike = values.every(v => {
    if (typeof v === 'boolean') return true
    if (typeof v === 'number') return v === 0 || v === 1
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase()
      return ['0', '1', 'true', 'false', 'on', 'off', 'yes', 'no'].includes(s)
    }
    return false
  })
  if (allBooleanLike) return 'on_off'

  const numericValues = values.filter(isNumericValue).map(v => Number(v))
  if (numericValues.length >= Math.max(4, Math.floor(values.length * 0.8))) {
    const unique = new Set(numericValues.map(n => n.toFixed(6))).size
    // Few distinct levels across many points is usually categorical/step-like.
    if (unique > 0 && unique <= 8) return 'bar'
    return 'line'
  }
  return 'line'
}

export function getChartLoadingMount (wrapper) {
  const inner = wrapper.querySelector('.chart-metric-card__inner')
  if (inner) return inner
  const ts = wrapper.querySelector('.time-series-line-chart-wrapper, .time-series-on-off-chart-wrapper')
  if (ts) return ts
  return wrapper
}

export function setChartLoading (wrapper, loading) {
  const mount = getChartLoadingMount(wrapper)
  if (!mount) return
  let el = mount.querySelector(':scope > .station-chart-live__loading')
  if (!el) {
    el = document.createElement('div')
    el.className = 'station-chart-live__loading'
    el.setAttribute('role', 'status')
    el.innerHTML =
      '<span class="station-chart-live__spinner" aria-hidden="true"></span>' +
      `<span class="station-chart-live__loading-text">${Drupal.t('Loading chart data...')}</span>`
    mount.appendChild(el)
  }
  el.classList.toggle('is-visible', loading)
  el.setAttribute('aria-hidden', loading ? 'true' : 'false')
  wrapper.setAttribute('aria-busy', loading ? 'true' : 'false')
}

export function resolveChartType (desiredType, allowedTypes, points) {
  const desired = (desiredType || 'auto').toLowerCase()
  const allowed = allowedTypes && allowedTypes.length ? allowedTypes : ['line', 'bar', 'on_off']

  if (desired !== 'auto') {
    return allowed.includes(desired) ? desired : (allowed[0] || 'line')
  }

  const detected = detectBestChartType(points)
  if (allowed.includes(detected)) return detected
  if (allowed.includes('line')) return 'line'
  return allowed[0] || 'line'
}

export function buildOnOffSeries (dataPoints) {
  const chartData = []
  let previousValue
  for (const point of dataPoints) {
    const timeInMs = new Date(point.timestamp).getTime()
    const val = coerceOnOffValue(point.value)
    if (previousValue !== undefined && previousValue !== val) {
      chartData.push([timeInMs, previousValue ? 1 : -1])
      chartData.push([timeInMs, val ? 1 : -1])
    } else {
      chartData.push([timeInMs, val ? 1 : -1])
    }
    previousValue = val
  }
  return chartData
}

export function renderLineChart (chartEl, dataPoints, timeRangeOverride, label = 'Value', units = '') {
  if (!chartEl) return
  const noData = chartEl.querySelector('.time-series-chart__no-data')
  if (!dataPoints || dataPoints.length === 0) {
    if (noData) noData.style.display = ''
    return
  }
  if (noData) noData.style.display = 'none'

  const existing = echarts.getInstanceByDom(chartEl)
  if (existing) existing.dispose()

  const chartData = dataPoints.map(point => [
    new Date(point.timestamp).getTime(),
    typeof point.value === 'number' ? point.value : parseFloat(point.value)
  ]).filter(row => !Number.isNaN(row[1]))

  if (chartData.length === 0) {
    if (noData) noData.style.display = ''
    return
  }

  if (!(timeRangeOverride.startTime && timeRangeOverride.endTime)) timeRangeOverride = undefined
  const minTs = timeRangeOverride?.startTime || chartData[0][0]
  const maxTs = timeRangeOverride?.endTime || chartData[chartData.length - 1][0]

  const lineColorValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--time-series-chart-red-color').trim() || '#911315'

  const myChart = echarts.init(chartEl)
  myChart.setOption({
    animation: false,
    grid: ECHART_OPTION_GRID_DEFAULTS,
    xAxis: {
      ...ECHART_OPTION_X_AXIS_DEFAULTS,
      min: minTs,
      max: maxTs
    },
    yAxis: ECHART_OPTION_Y_AXIS_DEFAULTS,
    series: [{
      type: 'line',
      data: chartData,
      smooth: true,
      showSymbol: false,
      lineStyle: { color: lineColorValue, width: 4 }
    }],
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        if (!params.length) return ''
        const date = new Date(params[0].value[0])
        const value = params[0].value[1]
        const dateStr = echarts.format.formatTime('MM/dd/yyyy', date)
        const ampm = determineAmPm(date)
        return `${dateStr} ${ampm}<br/>${label}: <b>${value}</b>${units ? ' ' + units : ''}`
      }
    }
  })

  let resizeTimeout
  const onResize = () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => myChart.resize(), 100)
  }
  window.addEventListener('resize', onResize)
}

export function renderBarChart (chartEl, dataPoints, timeRangeOverride, label = 'Value', units = '') {
  if (!chartEl) return
  const noData = chartEl.querySelector('.time-series-chart__no-data')
  if (!dataPoints || dataPoints.length === 0) {
    if (noData) noData.style.display = ''
    return
  }
  if (noData) noData.style.display = 'none'

  const existing = echarts.getInstanceByDom(chartEl)
  if (existing) existing.dispose()

  const chartData = dataPoints.map(point => [
    new Date(point.timestamp).getTime(),
    typeof point.value === 'number' ? point.value : parseFloat(point.value)
  ]).filter(row => !Number.isNaN(row[1]))

  if (chartData.length === 0) {
    if (noData) noData.style.display = ''
    return
  }

  if (!(timeRangeOverride.startTime && timeRangeOverride.endTime)) timeRangeOverride = undefined
  const minTs = timeRangeOverride?.startTime || chartData[0][0]
  const maxTs = timeRangeOverride?.endTime || chartData[chartData.length - 1][0]

  const barColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--time-series-chart-red-color').trim() || '#911315'

  const myChart = echarts.init(chartEl)
  myChart.setOption({
    animation: false,
    grid: ECHART_OPTION_GRID_DEFAULTS,
    xAxis: {
      ...ECHART_OPTION_X_AXIS_DEFAULTS,
      min: minTs,
      max: maxTs
    },
    yAxis: ECHART_OPTION_Y_AXIS_DEFAULTS,
    series: [{
      type: 'bar',
      data: chartData,
      barMaxWidth: 12,
      itemStyle: { color: barColor }
    }],
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        if (!params.length) return ''
        const date = new Date(params[0].value[0])
        const value = params[0].value[1]
        const dateStr = echarts.format.formatTime('MM/dd/yyyy', date)
        const ampm = determineAmPm(date)
        return `${dateStr} ${ampm}<br/>${label}: <b>${value}</b>${units ? ' ' + units : ''}`
      }
    }
  })
}

export function renderOnOffChart (chartEl, dataPoints, timeRangeOverride, label = 'Value', units = '') {
  if (!chartEl) return
  const noData = chartEl.querySelector('.time-series-chart__no-data')
  if (!dataPoints || dataPoints.length === 0) {
    if (noData) noData.style.display = ''
    return
  }
  if (noData) noData.style.display = 'none'

  const existing = echarts.getInstanceByDom(chartEl)
  if (existing) existing.dispose()

  const chartData = buildOnOffSeries(dataPoints)
  if (chartData.length === 0) {
    if (noData) noData.style.display = ''
    return
  }

  if (!(timeRangeOverride.startTime && timeRangeOverride.endTime)) timeRangeOverride = undefined
  const minTs = timeRangeOverride?.startTime || chartData[0][0]
  const maxTs = timeRangeOverride?.endTime || chartData[chartData.length - 1][0]

  const redColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-red').trim() || '#ef4444'
  const blueColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-navy').trim() || '#375F7D'

  const myChart = echarts.init(chartEl)
  myChart.setOption({
    animation: false,
    grid: ECHART_OPTION_GRID_DEFAULTS,
    xAxis: {
      ...ECHART_OPTION_X_AXIS_DEFAULTS,
      min: minTs,
      max: maxTs
    },
    yAxis: {
      ...ECHART_OPTION_Y_AXIS_DEFAULTS,
      min: -2,
      max: 2,
      axisLabel: {
        hideOverlap: true,
        fontSize: LABEL_FONT_SIZE,
        color: NORMAL_LABEL_COLOR,
        formatter: function (value) {
          if (value === 2) return '\n\nOn'
          if (value === -2) return 'Off\n\n'
          return ''
        }
      }
    },
    visualMap: {
      show: false,
      type: 'piecewise',
      pieces: [
        { min: -1, max: 0, color: blueColor },
        { min: 0, max: 1, color: redColor }
      ]
    },
    series: [{
      type: 'line',
      data: chartData,
      showSymbol: false,
      lineStyle: { width: 1 },
      areaStyle: { opacity: 1 }
    }],
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        if (params.length === 0) return ''

        const date = new Date(params[0].value[0])
        const value = params[0].value[1]
        const dateStr = echarts.format.formatTime('MM/dd/yyyy', date)
        const ampm = determineAmPm(date)

        return `${dateStr} ${ampm}<br/>${label}: <b>${value === 1 ? 1 : 0}</b>${units ? ' ' + units : ''}`
      }
    }
  })

  let resizeTimeout
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(function () {
      myChart.resize()
    }, 100)
  })
}
